import React, { useState, useEffect } from "react";
import axios from "axios";
import CustomNavbar from "../CustomPermissions/CustomNavbar";
import { API_BASE_URL } from "../../config/api";
import LoadingSpinner from "../common/LoadingSpinner";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // Dropdown options
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [customPermissions, setCustomPermissions] = useState([]);

  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showSuperuserModal, setShowSuperuserModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [copySuccess, setCopySuccess] = useState("");
  const [showDualRoleModal, setShowDualRoleModal] = useState(false);
  const [userForDualRole, setUserForDualRole] = useState(null);
  const [dualRoleData, setDualRoleData] = useState({
    department_id: "",
    designation_id: "",
    target: 0,
    revenue: 0
  });

  // Form states
  const [newRole, setNewRole] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  // Create user form states
  const [createUserForm, setCreateUserForm] = useState({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "regular",
  });

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowPassword(true);
  };

  const copyToClipboard = () => {
    if (!newPassword) return;
    navigator.clipboard.writeText(newPassword).then(() => {
      setCopySuccess("Copied!");
      setTimeout(() => setCopySuccess(""), 2000);
    }, (err) => {
      console.error('Could not copy text: ', err);
    });
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchTerm, roleFilter, users]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = localStorage.getItem("access_token");

      // Fetch all users
      const usersResponse = await axios.get(
        `${API_BASE_URL}/Authentication/manage/users/all/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );


      // Fetch departments and designations
      const optionsResponse = await axios.get(
        `${API_BASE_URL}/Authentication/manage/options/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      // Fetch available permissions
      const permissionsResponse = await axios.get(
        `${API_BASE_URL}/Authentication/manage/available-permissions/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setUsers(usersResponse.data.users || []);
      setDepartments(optionsResponse.data.departments || []);
      setDesignations(optionsResponse.data.designations || []);
      setCustomPermissions(permissionsResponse.data.custom_permissions || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else if (err.response && err.response.status === 403) {
        setError("Failed to load user data. Please ensure you have superuser permissions.");
      } else {
        setError("An unexpected error occurred while fetching user data.");
      }
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Role filter
    if (roleFilter !== "all") {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    const currentRole =
      user.role === "HR Associate" ? "hr" :
        user.role === "Docladder Member" ? "docladder" :
          user.role === "Team Leader" ? "team_leader" : "regular";
    setNewRole(currentRole);

    // Initialize permissions for regular users
    if (currentRole === "regular") {
      setSelectedPermissions(user.custom_permissions?.map(p => p.id) || []);
    } else {
      setSelectedPermissions([]);
    }

    setSelectedDepartment("");
    setSelectedDesignation("");
    setShowRoleModal(true);
  };

  const openDepartmentModal = (user) => {
    setSelectedUser(user);
    setSelectedDepartment("");
    setSelectedDesignation("");
    setShowDepartmentModal(true);
  };

  const openPermissionsModal = async (user) => {
    setSelectedUser(user);
    setLoading(true);

    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.get(
        `${API_BASE_URL}/Authentication/manage/user/${user.id}/permissions/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setSelectedPermissions(
        response.data.custom_permissions?.map((p) => p.id) || []
      );
    } catch (err) {
      console.error("Error fetching user permissions:", err);
      setError("Failed to load user permissions");
    } finally {
      setLoading(false);
    }

    setShowPermissionsModal(true);
  };

  const openSuperuserModal = (user) => {
    setSelectedUser(user);
    setShowSuperuserModal(true);
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const openPasswordResetModal = (user) => {
    setUserToResetPassword(user);
    setNewPassword("");
    setShowPassword(false);
    setCopySuccess("");
    setShowPasswordResetModal(true);
  };

  const openDualRoleModal = (user) => {
    setUserForDualRole(user);
    setDualRoleData({
      department_id: "",
      designation_id: "",
      target: 0,
      revenue: 0
    });
    setShowDualRoleModal(true);
  };

  const handleAssignDualRole = async () => {
    if (!userForDualRole || !dualRoleData.department_id) {
      setError("Department is required for dual role");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const accessToken = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/Authentication/manage/user/${userForDualRole.id}/assign-dual-role/`,
        dualRoleData,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setSuccess(`Dual role assigned successfully to "${userForDualRole.username}"!`);
      setShowDualRoleModal(false);
      setUserForDualRole(null);
      fetchAllData();
    } catch (err) {
      console.error("Error assigning dual role:", err);
      setError(err.response?.data?.error || "Failed to assign dual role");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!userToResetPassword || !newPassword) {
      setError("Password is required");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const accessToken = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/Authentication/manage/user/${userToResetPassword.id}/reset-password/`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setSuccess(`Password reset successfully for "${userToResetPassword.username}"!`);
      setShowPasswordResetModal(false);
      setUserToResetPassword(null);
      setNewPassword("");
    } catch (err) {
      console.error("Error resetting password:", err);
      setError(err.response?.data?.error || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const accessToken = localStorage.getItem("access_token");
      await axios.delete(
        `${API_BASE_URL}/Authentication/manage/user/${userToDelete.id}/delete/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setSuccess(`User "${userToDelete.username}" deleted successfully!`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      fetchAllData();
    } catch (err) {
      console.error("Error deleting user:", err);
      setError(err.response?.data?.error || "Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRole = async () => {
    if (!selectedUser || !newRole) return;

    // Validate regular user has at least one permission
    if (newRole === "regular" && selectedPermissions.length === 0) {
      setError("Regular users must have at least one permission selected");
      return;
    }

    // Validate team leader has department
    if (newRole === "team_leader" && !selectedDepartment) {
      setError("Team Leaders must have a department selected");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const accessToken = localStorage.getItem("access_token");
      const requestData = {
        role: newRole
      };

      // Add department/designation if needed
      if (newRole === "team_leader" && selectedDepartment) {
        requestData.department_id = selectedDepartment;
      }
      if (selectedDesignation && newRole !== "regular") {
        requestData.designation_id = selectedDesignation;
      }

      // Add custom permissions for regular users
      if (newRole === "regular") {
        requestData.custom_permissions = selectedPermissions;
      }

      await axios.post(
        `${API_BASE_URL}/Authentication/manage/user/${selectedUser.id}/change-role/`,
        requestData,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setSuccess(`User role changed to ${newRole} successfully!`);
      setShowRoleModal(false);
      setSelectedPermissions([]);
      fetchAllData();
    } catch (err) {
      console.error("Error changing role:", err);
      setError(err.response?.data?.error || "Failed to change user role");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeDepartment = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const accessToken = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/Authentication/manage/user/${selectedUser.id}/change-department/`,
        {
          department_id: selectedDepartment || null,
          designation_id: selectedDesignation || null
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setSuccess("Department/Designation updated successfully!");
      setShowDepartmentModal(false);
      fetchAllData();
    } catch (err) {
      console.error("Error changing department:", err);
      setError(err.response?.data?.error || "Failed to change department");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePermissions = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const accessToken = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/Authentication/manage/user/${selectedUser.id}/permissions/update/`,
        {
          custom_permissions: selectedPermissions,
          django_permissions: [], // Keep existing Django permissions
          is_staff: selectedUser.is_staff
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setSuccess("Permissions updated successfully!");
      setShowPermissionsModal(false);
      fetchAllData();
    } catch (err) {
      console.error("Error updating permissions:", err);
      setError(err.response?.data?.error || "Failed to update permissions");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSuperuser = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const accessToken = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/Authentication/manage/user/${selectedUser.id}/toggle-superuser/`,
        {
          is_superuser: !selectedUser.is_superuser
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const action = !selectedUser.is_superuser ? "promoted to" : "demoted from";
      setSuccess(`User ${action} superuser successfully!`);
      setShowSuperuserModal(false);
      fetchAllData();
    } catch (err) {
      console.error("Error toggling superuser:", err);
      setError(err.response?.data?.error || "Failed to toggle superuser status");
    } finally {
      setLoading(false);
    }
  };

  const openCreateUserModal = () => {
    // Reset form
    setCreateUserForm({
      username: "",
      email: "",
      password: "",
      first_name: "",
      last_name: "",
      role: "regular",
    });
    setSelectedDepartment("");
    setSelectedDesignation("");
    setSelectedPermissions([]);
    setDualRoleData({ department_id: "", designation_id: "", target: 0, revenue: 0 });
    setShowCreateUserModal(true);
  };

  const handleCreateUser = async () => {
    // Validation
    if (!createUserForm.username || !createUserForm.password) {
      setError("Username and password are required");
      return;
    }

    if (createUserForm.role === "regular" && selectedPermissions.length === 0) {
      setError("Regular users must have at least one permission selected");
      return;
    }

    if ((createUserForm.role === "team_leader" || createUserForm.role === "dual_role") && !selectedDepartment) {
      setError("Team Leaders and Dual Role users must have a department selected");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const accessToken = localStorage.getItem("access_token");

      // Step 1: Create Django user
      const userData = {
        username: createUserForm.username,
        password: createUserForm.password,
        email: createUserForm.email || "",
        first_name: createUserForm.first_name || "",
        last_name: createUserForm.last_name || "",
      };

      const createResponse = await axios.post(
        `${API_BASE_URL}/Authentication/create-user/`,
        userData,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const newUserId = createResponse.data.user_id;

      // Step 2: Assign role
      if (createUserForm.role === "dual_role") {
        // For dual role, use the assign-dual-role endpoint
        const dualRoleRequestData = {
          department_id: selectedDepartment,
          designation_id: selectedDesignation || null,
          target: dualRoleData.target || 0,
          revenue: dualRoleData.revenue || 0
        };

        await axios.post(
          `${API_BASE_URL}/Authentication/manage/user/${newUserId}/assign-dual-role/`,
          dualRoleRequestData,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      } else {
        // For other roles, use the change-role endpoint
        const roleData = {
          role: createUserForm.role,
        };

        if (createUserForm.role === "team_leader") {
          roleData.department_id = selectedDepartment;
        }
        if (selectedDesignation) {
          roleData.designation_id = selectedDesignation;
        }
        if (createUserForm.role === "regular") {
          roleData.custom_permissions = selectedPermissions;
        }

        await axios.post(
          `${API_BASE_URL}/Authentication/manage/user/${newUserId}/change-role/`,
          roleData,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      }

      setSuccess(`User created successfully as ${createUserForm.role.replace('_', ' ')}!`);
      setShowCreateUserModal(false);
      // Reset dual role data
      setDualRoleData({ department_id: "", designation_id: "", target: 0, revenue: 0 });
      fetchAllData();
    } catch (err) {
      console.error("Error creating user:", err);
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "Super Admin":
        return "bg-red-100 text-red-800 border-red-300";
      case "HR Associate":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "Docladder Member":
        return "bg-purple-100 text-purple-800 border-purple-300";
      case "Team Leader":
        return "bg-green-100 text-green-800 border-green-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  if (loading && users.length === 0) {
    return (
      <div style={{ display: "flex" }}>
        <CustomNavbar />
        <div className="flex-1">
          <LoadingSpinner message="Loading user management..." />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex" }}>
      <CustomNavbar />
      <div className="flex-1 p-6 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex justify-between items-start">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text mb-2">
                👥 User Management System
              </h1>
              <p className="text-gray-600">Manage user roles, departments, and permissions</p>
            </div>
            <button
              onClick={openCreateUserModal}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 rounded-lg hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl transition-all duration-200 font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Create User
            </button>
          </div>

          {/* Alerts */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-700 font-bold">×</button>
            </div>
          )}

          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
              <span>{success}</span>
              <button onClick={() => setSuccess(null)} className="text-green-700 font-bold">×</button>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Users</label>
                <input
                  type="text"
                  name="user_search_q"
                  id="user_search_q"
                  autoComplete="off"
                  data-lpignore="true"
                  placeholder="Search by username, email, or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Role</label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Roles</option>
                  <option value="Super Admin">Super Admin</option>
                  <option value="HR Associate">HR Associate</option>
                  <option value="Docladder Member">Docladder Member</option>
                  <option value="Team Leader">Team Leader</option>
                  <option value="Regular User">Regular User</option>
                </select>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-4 border-t-4 border-blue-500">
              <div className="text-2xl font-bold text-blue-600">{users.length}</div>
              <div className="text-sm text-gray-600">Total Users</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4 border-t-4 border-red-500">
              <div className="text-2xl font-bold text-red-600">
                {users.filter(u => u.role === "Super Admin").length}
              </div>
              <div className="text-sm text-gray-600">Super Admins</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4 border-t-4 border-blue-500">
              <div className="text-2xl font-bold text-blue-600">
                {users.filter(u => u.role === "HR Associate").length}
              </div>
              <div className="text-sm text-gray-600">HR Associates</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4 border-t-4 border-purple-500">
              <div className="text-2xl font-bold text-purple-600">
                {users.filter(u => u.role === "Docladder Member").length}
              </div>
              <div className="text-sm text-gray-600">Docladder</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4 border-t-4 border-green-500">
              <div className="text-2xl font-bold text-green-600">
                {users.filter(u => u.role === "Team Leader").length}
              </div>
              <div className="text-sm text-gray-600">Team Leaders</div>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Department</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Designation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Password</th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {user.image_url ? (
                            <img
                              src={`${API_BASE_URL}${user.image_url}`}
                              alt={user.username}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-gray-600 font-bold">
                                {user.username.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.username}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRoleBadgeColor(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.department || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.designation || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_active ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-400 font-mono">********</span>
                          <span
                            role="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openPasswordResetModal(user);
                            }}
                            className="text-orange-600 hover:text-orange-900 text-xs px-2 py-1 border border-orange-600 rounded hover:bg-orange-50 transition cursor-pointer"
                            title="Reset Password"
                          >
                            Reset
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => openRoleModal(user)}
                            className="text-blue-600 hover:text-blue-900 px-2 py-1 border border-blue-600 rounded hover:bg-blue-50 transition"
                            title="Change Role"
                          >
                            🔄 Role
                          </button>
                          <button
                            onClick={() => openDepartmentModal(user)}
                            className="text-purple-600 hover:text-purple-900 px-2 py-1 border border-purple-600 rounded hover:bg-purple-50 transition"
                            title="Change Department"
                          >
                            🏢 Dept
                          </button>
                          <button
                            onClick={() => openPermissionsModal(user)}
                            className="text-green-600 hover:text-green-900 px-2 py-1 border border-green-600 rounded hover:bg-green-50 transition"
                            title="Manage Permissions"
                          >
                            🔐 Perms
                          </button>
                          {!user.is_superuser && (
                            <button
                              onClick={() => openSuperuserModal(user)}
                              className="text-red-600 hover:text-red-900 px-2 py-1 border border-red-600 rounded hover:bg-red-50 transition"
                              title="Promote to Superuser"
                            >
                              ⭐ Super
                            </button>
                          )}                          {!user.is_superuser && (
                            <button
                              onClick={() => openDualRoleModal(user)}
                              className="text-purple-600 hover:text-purple-900 px-2 py-1 border border-purple-600 rounded hover:bg-purple-50 transition"
                              title="Assign Dual Role (Leader + HR)"
                            >
                              👥 Dual Role
                            </button>
                          )}
                          {!user.is_superuser && (
                            <button
                              onClick={() => openDeleteModal(user)}
                              className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded transition"
                              title="Delete User"
                            >
                              🗑️ Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <div className="text-6xl mb-4">👥</div>
                <div className="text-lg">No users found matching your criteria</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Role Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full my-8 flex flex-col" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
            {/* Modal Header - Fixed */}
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800">Change User Role</h3>
              <p className="text-gray-600 text-sm mt-1">
                Change role for: <strong>{selectedUser.username}</strong>
              </p>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">New Role</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="hr">HR Associate</option>
                  <option value="docladder">Docladder Member</option>
                  <option value="team_leader">Team Leader</option>
                  <option value="regular">Regular User</option>
                </select>
              </div>

              {newRole === "team_leader" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department (Required)</label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {(newRole === "hr" || newRole === "docladder" || newRole === "team_leader") && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Designation (Optional)</label>
                  <select
                    value={selectedDesignation}
                    onChange={(e) => setSelectedDesignation(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Designation</option>
                    {designations.map((des) => (
                      <option key={des.id} value={des.id}>
                        {des.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {newRole === "regular" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Permissions (Required - at least one)
                  </label>
                  <div className="border border-gray-300 rounded-md p-3 space-y-2 bg-gray-50">
                    {customPermissions.map((perm) => (
                      <label
                        key={perm.id}
                        className="flex items-center space-x-3 p-2 hover:bg-blue-50 rounded-md cursor-pointer border border-transparent hover:border-blue-200 transition-all bg-white"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissions.includes(perm.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPermissions([...selectedPermissions, perm.id]);
                            } else {
                              setSelectedPermissions(selectedPermissions.filter((id) => id !== perm.id));
                            }
                          }}
                          className="h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                        />
                        <span className="text-sm font-medium text-gray-700">{perm.name}</span>
                      </label>
                    ))}
                  </div>
                  {selectedPermissions.length === 0 && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Please select at least one permission
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer - Fixed */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 rounded-b-lg flex-shrink-0">
              <button
                onClick={() => setShowRoleModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeRole}
                disabled={
                  loading ||
                  (newRole === "team_leader" && !selectedDepartment) ||
                  (newRole === "regular" && selectedPermissions.length === 0)
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
              >
                {loading ? "Changing..." : "Change Role"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Department Modal */}
      {showDepartmentModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full my-8">
            {/* Modal Header - Fixed */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">Change Department/Designation</h3>
              <p className="text-gray-600 text-sm mt-1">
                Update for: <strong>{selectedUser.username}</strong>
              </p>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              {selectedUser.role === "Team Leader" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Keep Current</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                <select
                  value={selectedDesignation}
                  onChange={(e) => setSelectedDesignation(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Keep Current</option>
                  {designations.map((des) => (
                    <option key={des.id} value={des.id}>
                      {des.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowDepartmentModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeDepartment}
                disabled={loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 font-medium transition-colors"
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full my-8 flex flex-col" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
            {/* Modal Header - Fixed */}
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800">Manage Permissions</h3>
              <p className="text-gray-600 text-sm mt-1">
                Assign custom permissions for: <strong>{selectedUser.username}</strong>
              </p>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
              <div className="space-y-2">
                {customPermissions.map((perm) => (
                  <label
                    key={perm.id}
                    className="flex items-center space-x-3 p-3 hover:bg-blue-50 rounded-md cursor-pointer border border-transparent hover:border-blue-200 transition-all"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(perm.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPermissions([...selectedPermissions, perm.id]);
                        } else {
                          setSelectedPermissions(selectedPermissions.filter((id) => id !== perm.id));
                        }
                      }}
                      className="h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                    />
                    <span className="text-sm font-medium text-gray-700">{perm.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 rounded-b-lg flex-shrink-0">
              <button
                onClick={() => setShowPermissionsModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePermissions}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 font-medium transition-colors"
              >
                {loading ? "Updating..." : "Update Permissions"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Superuser Modal */}
      {showSuperuserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full my-8">
            {/* Modal Header - Fixed */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">
                {selectedUser.is_superuser ? "Remove Superuser Status" : "Promote to Superuser"}
              </h3>
              <p className="text-gray-600 text-sm mt-1">
                Are you sure you want to {selectedUser.is_superuser ? "remove superuser status from" : "promote"}{" "}
                <strong>{selectedUser.username}</strong>?
              </p>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              {!selectedUser.is_superuser && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong className="font-semibold">Warning:</strong> Superusers have full access to all system features and can manage other users.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer - Fixed */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowSuperuserModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleSuperuser}
                disabled={loading}
                className={`px-4 py-2 text-white rounded-md disabled:opacity-50 font-medium transition-colors ${selectedUser.is_superuser
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-green-600 hover:bg-green-700"
                  }`}
              >
                {loading ? "Processing..." : selectedUser.is_superuser ? "Remove" : "Promote"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8 flex flex-col" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800">Create New User</h3>
              <p className="text-gray-600 text-sm mt-1">
                Create a new user account and assign role with permissions
              </p>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
              {/* Basic Info */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-700 mb-3">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={createUserForm.username}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, username: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter username"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={createUserForm.password}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter password"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={createUserForm.email}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="user@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                    <input
                      type="text"
                      value={createUserForm.first_name}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, first_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="First name"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      value={createUserForm.last_name}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, last_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Last name"
                    />
                  </div>
                </div>
              </div>

              {/* Role Selection */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-700 mb-3">Role & Permissions</h4>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={createUserForm.role}
                    onChange={(e) => setCreateUserForm({ ...createUserForm, role: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="regular">Regular User</option>
                    <option value="hr">HR Associate</option>
                    <option value="docladder">Docladder Member</option>
                    <option value="team_leader">Team Leader</option>
                    <option value="dual_role">Team Leader + HR Associate (Dual Role)</option>
                  </select>
                </div>

                {/* Info box for dual role */}
                {createUserForm.role === "dual_role" && (
                  <div className="mb-4 p-4 bg-purple-50 border-l-4 border-purple-500 rounded">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-purple-700">
                          <strong>Dual Role:</strong> User will be able to toggle between Team Leader and HR Associate modes with a button in their sidebar.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Department for Team Leaders and Dual Role */}
                {(createUserForm.role === "team_leader" || createUserForm.role === "dual_role") && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={selectedDepartment}
                      onChange={(e) => setSelectedDepartment(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Department</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Target and Revenue for Dual Role */}
                {createUserForm.role === "dual_role" && (
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Candidates (HR)
                      </label>
                      <input
                        type="number"
                        value={dualRoleData.target}
                        onChange={(e) => setDualRoleData({ ...dualRoleData, target: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        min="0"
                        placeholder="e.g., 50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Revenue (lacs)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={dualRoleData.revenue}
                        onChange={(e) => setDualRoleData({ ...dualRoleData, revenue: parseFloat(e.target.value) || 0 })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        min="0"
                        placeholder="e.g., 10.5"
                      />
                    </div>
                  </div>
                )}

                {/* Designation (Optional for all employee types) */}
                {(createUserForm.role === "hr" || createUserForm.role === "docladder" || createUserForm.role === "team_leader" || createUserForm.role === "dual_role") && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Designation (Optional)</label>
                    <select
                      value={selectedDesignation}
                      onChange={(e) => setSelectedDesignation(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Designation</option>
                      {designations.map((des) => (
                        <option key={des.id} value={des.id}>
                          {des.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Permissions for Regular Users */}
                {createUserForm.role === "regular" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Permissions <span className="text-red-500">*</span> (at least one)
                    </label>
                    <div className="border border-gray-300 rounded-md p-3 space-y-2 bg-gray-50 max-h-64 overflow-y-auto">
                      {customPermissions.map((perm) => (
                        <label
                          key={perm.id}
                          className="flex items-center space-x-3 p-2 hover:bg-blue-50 rounded-md cursor-pointer border border-transparent hover:border-blue-200 transition-all bg-white"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPermissions.includes(perm.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPermissions([...selectedPermissions, perm.id]);
                              } else {
                                setSelectedPermissions(selectedPermissions.filter((id) => id !== perm.id));
                              }
                            }}
                            className="h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                          />
                          <span className="text-sm font-medium text-gray-700">{perm.name}</span>
                        </label>
                      ))}
                    </div>
                    {selectedPermissions.length === 0 && (
                      <p className="text-red-500 text-sm mt-2 flex items-center">
                        <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Please select at least one permission
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 rounded-b-lg flex-shrink-0">
              <button
                onClick={() => setShowCreateUserModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={
                  loading ||
                  !createUserForm.username ||
                  !createUserForm.password ||
                  (createUserForm.role === "team_leader" && !selectedDepartment) ||
                  (createUserForm.role === "dual_role" && !selectedDepartment) ||
                  (createUserForm.role === "regular" && selectedPermissions.length === 0)
                }
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 font-medium transition-colors flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dual Role Assignment Modal */}
      {showDualRoleModal && userForDualRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-purple-50">
              <h3 className="text-xl font-bold text-purple-800">Assign Dual Role</h3>
              <p className="text-gray-600 text-sm mt-1">
                Assign Team Leader + HR Associate role to: <strong>{userForDualRole.username}</strong>
              </p>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4">
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Dual Role:</strong> User will be able to toggle between Team Leader and HR Associate modes.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department (Team Leader) <span className="text-red-500">*</span>
                </label>
                <select
                  value={dualRoleData.department_id}
                  onChange={(e) => setDualRoleData({ ...dualRoleData, department_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Designation (Optional)
                </label>
                <select
                  value={dualRoleData.designation_id}
                  onChange={(e) => setDualRoleData({ ...dualRoleData, designation_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select Designation</option>
                  {designations.map((des) => (
                    <option key={des.id} value={des.id}>
                      {des.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Candidates (HR)
                </label>
                <input
                  type="number"
                  value={dualRoleData.target}
                  onChange={(e) => setDualRoleData({ ...dualRoleData, target: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Revenue in lacs (HR)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={dualRoleData.revenue}
                  onChange={(e) => setDualRoleData({ ...dualRoleData, revenue: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="0"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => {
                  setShowDualRoleModal(false);
                  setUserForDualRole(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignDualRole}
                disabled={loading || !dualRoleData.department_id}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 font-medium transition-colors flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Assigning...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Assign Dual Role
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordResetModal && userToResetPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-orange-50">
              <h3 className="text-xl font-bold text-orange-800">Reset User Password</h3>
              <p className="text-gray-600 text-sm mt-1">
                Resetting password for: <strong>{userToResetPassword.username}</strong>
              </p>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-10"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded transition text-sm flex items-center justify-center gap-2"
                  >
                    <span>🎲</span> Generate Secure
                  </button>
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded transition text-sm flex items-center gap-2"
                    title="Copy to clipboard"
                  >
                    <span>📋</span> {copySuccess || "Copy"}
                  </button>
                </div>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> The user will need to use this new password to log in. Make sure to communicate it securely.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordResetModal(false);
                  setUserToResetPassword(null);
                  setNewPassword("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePasswordReset}
                disabled={loading || !newPassword || newPassword.length < 6}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 font-medium transition-colors flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Resetting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Reset Password
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="ml-3 text-xl font-bold text-red-800">Delete User</h3>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the user <strong className="text-red-600">"{userToDelete.username}"</strong>?
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Warning:</strong> This action cannot be undone. All user data including records, tasks, and permissions will be permanently deleted.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                User Details:
              </p>
              <ul className="mt-2 text-sm text-gray-700 space-y-1">
                <li><strong>Username:</strong> {userToDelete.username}</li>
                <li><strong>Email:</strong> {userToDelete.email || 'N/A'}</li>
                <li><strong>Role:</strong> {userToDelete.role || 'N/A'}</li>
              </ul>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 font-medium transition-colors flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

